import React, { useState, useEffect, useContext } from "react";
import {
    Card,
    CardContent,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Paper,
    CircularProgress,
    Box,
    Alert,
    IconButton,
    Tooltip,
    InputAdornment,
} from "@mui/material";
import {
    History,
    TrendingUp,
    TrendingDown,
    AccountBalance,
    Receipt,
    ArrowBack,
    FilterList,
    FolderOpen,
    Person,
    Category,
    DateRange,
    Close,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { AdminProjectContext } from "../../contexts/AdminProjectContext";
import { AdminUserContext } from "../../contexts/AdminUserContext";
import { CommonContext } from "../../contexts/CommonContext";
import useAxios from "../../hooks/useAxios";
import { ADMIN_VIEWS, ADMIN_TRANSACTIONS_URL } from "../../utils/constant";
import { Transaction, TransactionFilters, TransactionStatistics } from "../../interface/Transaction.interface";
import "../../styles/AdminTransactionHistory.css";
import { logger } from '../../utils/logger';
import { formatAmount, formatNumber } from '../../utils/utilities';

interface AdminTransactionHistoryProps {
    selectedMenu: (view: number) => void;
}

export default function AdminTransactionHistory({ selectedMenu }: AdminTransactionHistoryProps) {
    const { projects } = useContext(AdminProjectContext);
    const { users } = useContext(AdminUserContext);
    const { previousMenu } = useContext(CommonContext);
    const { getWithAxios } = useAxios();

    // États
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [statistics, setStatistics] = useState<TransactionStatistics>({
        totalContributions: 0,
        totalWithdrawals: 0,
        netBalance: 0,
        transactionCount: 0,
        pendingCount: 0,
        completedCount: 0,
        failedCount: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // Filtres
    const [filters, setFilters] = useState<TransactionFilters>({
        projectUuid: "ALL",
        userUuid: "ALL",
        type: "ALL",
        startDate: undefined,
        endDate: undefined,
    });

    // Liste des utilisateurs filtrés par projet
    const filteredUsers = filters.projectUuid && filters.projectUuid !== "ALL"
        ? projects?.find(p => p.uuid === filters.projectUuid)?.Users || []
        : Array.isArray(users) ? users : [];

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Charger les transactions depuis le backend
    useEffect(() => {
        fetchTransactions();
    }, [filters, page, rowsPerPage]);

    const fetchTransactions = async () => {
        setIsLoading(true);
        setError("");

        try {
            // Construire les query params
            const params = new URLSearchParams();

            if (filters.projectUuid && filters.projectUuid !== "ALL") {
                params.append("projectUuid", filters.projectUuid);
            }

            if (filters.userUuid && filters.userUuid !== "ALL") {
                params.append("userUuid", filters.userUuid);
            }

            if (filters.type && filters.type !== "ALL") {
                params.append("type", filters.type);
            }

            if (filters.startDate) {
                params.append("startDate", filters.startDate);
            }

            if (filters.endDate) {
                params.append("endDate", filters.endDate);
            }

            params.append("page", String(page + 1)); // Backend page commence à 1
            params.append("limit", String(rowsPerPage));

            const url = `${ADMIN_TRANSACTIONS_URL}?${params.toString()}`;
            const response = await getWithAxios(url);

            if (response.success) {
                setTransactions(response.transactions || []);
                setStatistics(response.statistics || {
                    totalContributions: 0,
                    totalWithdrawals: 0,
                    netBalance: 0,
                    transactionCount: 0,
                    pendingCount: 0,
                    completedCount: 0,
                    failedCount: 0,
                });
                setTotalCount(response.pagination?.total || 0);
            } else {
                setError("Erreur lors du chargement des transactions");
            }
        } catch (err) {
            logger.error('Erreur lors de la récupération des transactions:', err);
            setError('Erreur lors du chargement des transactions');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (newFilters: Partial<TransactionFilters>) => {
        // Si on change de projet, réinitialiser le filtre utilisateur
        if (newFilters.projectUuid !== undefined && newFilters.projectUuid !== filters.projectUuid) {
            setFilters({ ...filters, ...newFilters, userUuid: "ALL" });
        } else {
            setFilters({ ...filters, ...newFilters });
        }
        setPage(0); // Reset to first page when filters change
    };

    const handleBackClick = () => selectedMenu(previousMenu || ADMIN_VIEWS.DASHBOARD_VIEW);

    const hasActiveFilters = () => {
        return (
            (filters.projectUuid && filters.projectUuid !== "ALL") ||
            (filters.userUuid && filters.userUuid !== "ALL") ||
            (filters.type && filters.type !== "ALL") ||
            filters.startDate ||
            filters.endDate
        );
    };

    const clearAllFilters = () => {
        setFilters({
            projectUuid: "ALL",
            userUuid: "ALL",
            type: "ALL",
            startDate: undefined,
            endDate: undefined,
        });
        setPage(0);
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.projectUuid && filters.projectUuid !== "ALL") count++;
        if (filters.userUuid && filters.userUuid !== "ALL") count++;
        if (filters.type && filters.type !== "ALL") count++;
        if (filters.startDate) count++;
        if (filters.endDate) count++;
        return count;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "COMPLETED": return "success";
            case "PENDING": return "warning";
            case "FAILED": return "error";
            default: return "default";
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case "CONTRIBUTION": return "primary";
            case "WITHDRAWAL": return "secondary";
            case "PENALTY": return "error";
            default: return "default";
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case "COMPLETED": return "Complété";
            case "PENDING": return "En attente";
            case "FAILED": return "Échoué";
            default: return status;
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case "CONTRIBUTION": return "Encaissement";
            case "WITHDRAWAL": return "Retrait";
            case "PENALTY": return "Pénalité";
            default: return type;
        }
    };

    const getEmptyMessage = () => {
        if (filters.type === "WITHDRAWAL") {
            return "Aucun retrait disponible";
        }
        if (filters.type === "CONTRIBUTION") {
            return "Aucun encaissement disponible";
        }
        if (filters.type === "PENALTY") {
            return "Aucune pénalité disponible";
        }
        return "Aucune transaction trouvée";
    };

    return (
        <div className="transaction-history-container">
            <div className="section-header">
                <div className="header-left">
                    <div className="section-icon">
                        <History />
                    </div>
                    <h1 className="section-title-transaction">Historique des Transactions</h1>
                </div>
                <button className="back-button" onClick={handleBackClick}>
                    <ArrowBack /> Retour
                </button>
            </div>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* KPI Cards */}
            <div className="kpi-cards">
                <Card className="kpi-card kpi-success">
                    <CardContent>
                        <div className="kpi-icon">
                            <TrendingUp />
                        </div>
                        <div className="kpi-content">
                            <p className="kpi-label">Total Encaissements</p>
                            <h3 className="kpi-value">{formatAmount(statistics.totalContributions)}</h3>
                        </div>
                    </CardContent>
                </Card>

                <Card className="kpi-card kpi-warning">
                    <CardContent>
                        <div className="kpi-icon">
                            <TrendingDown />
                        </div>
                        <div className="kpi-content">
                            <p className="kpi-label">Total Retraits</p>
                            <h3 className="kpi-value">{formatAmount(statistics.totalWithdrawals)}</h3>
                        </div>
                    </CardContent>
                </Card>

                <Card className="kpi-card kpi-info">
                    <CardContent>
                        <div className="kpi-icon">
                            <AccountBalance />
                        </div>
                        <div className="kpi-content">
                            <p className="kpi-label">Solde Net</p>
                            <h3 className="kpi-value">{formatAmount(statistics.netBalance)}</h3>
                        </div>
                    </CardContent>
                </Card>

                <Card className="kpi-card kpi-primary">
                    <CardContent>
                        <div className="kpi-icon">
                            <Receipt />
                        </div>
                        <div className="kpi-content">
                            <p className="kpi-label">Nombre de Transactions</p>
                            <h3 className="kpi-value">{statistics.completedCount}</h3>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filtres */}
            <Card className="filters-card">
                <CardContent>
                    <div className="filters-header">
                        <div className="filters-header-left">
                            <div className="filters-icon">
                                <FilterList />
                            </div>
                            <h3 className="filters-title">Filtres</h3>
                            {hasActiveFilters() && (
                                <Chip
                                    label={`${getActiveFiltersCount()} actif${getActiveFiltersCount() > 1 ? 's' : ''}`}
                                    size="small"
                                    className="filters-count-chip"
                                />
                            )}
                        </div>
                        {hasActiveFilters() && (
                            <Tooltip title="Effacer tous les filtres">
                                <button className="clear-filters-btn" onClick={clearAllFilters}>
                                    <Close fontSize="small" />
                                    Effacer
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {/* Active Filters Chips */}
                    {hasActiveFilters() && (
                        <div className="active-filters-chips">
                            {filters.projectUuid && filters.projectUuid !== "ALL" && (
                                <Chip
                                    label={projects?.find(p => p.uuid === filters.projectUuid)?.name || "Projet"}
                                    onDelete={() => handleFilterChange({ projectUuid: "ALL" })}
                                    size="small"
                                    className="active-filter-chip"
                                    icon={<FolderOpen style={{ fontSize: 16 }} />}
                                />
                            )}
                            {filters.userUuid && filters.userUuid !== "ALL" && (
                                <Chip
                                    label={filteredUsers?.find(u => u.uuid === filters.userUuid)?.firstname || "Utilisateur"}
                                    onDelete={() => handleFilterChange({ userUuid: "ALL" })}
                                    size="small"
                                    className="active-filter-chip"
                                    icon={<Person style={{ fontSize: 16 }} />}
                                />
                            )}
                            {filters.type && filters.type !== "ALL" && (
                                <Chip
                                    label={getTypeLabel(filters.type)}
                                    onDelete={() => handleFilterChange({ type: "ALL" })}
                                    size="small"
                                    className="active-filter-chip"
                                    icon={<Category style={{ fontSize: 16 }} />}
                                />
                            )}
                            {filters.startDate && (
                                <Chip
                                    label={`Depuis ${dayjs(filters.startDate).format('DD/MM/YYYY')}`}
                                    onDelete={() => handleFilterChange({ startDate: undefined })}
                                    size="small"
                                    className="active-filter-chip"
                                    icon={<DateRange style={{ fontSize: 16 }} />}
                                />
                            )}
                            {filters.endDate && (
                                <Chip
                                    label={`Jusqu'au ${dayjs(filters.endDate).format('DD/MM/YYYY')}`}
                                    onDelete={() => handleFilterChange({ endDate: undefined })}
                                    size="small"
                                    className="active-filter-chip"
                                    icon={<DateRange style={{ fontSize: 16 }} />}
                                />
                            )}
                        </div>
                    )}

                    <div className="filters-grid">
                        <FormControl fullWidth className="filter-field">
                            <InputLabel shrink>Projet</InputLabel>
                            <Select
                                value={filters.projectUuid || "ALL"}
                                onChange={(e) => handleFilterChange({ projectUuid: e.target.value })}
                                displayEmpty
                                startAdornment={
                                    <InputAdornment position="start">
                                        <FolderOpen className="filter-field-icon" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="ALL">Tous les projets</MenuItem>
                                {projects?.map((project) => (
                                    <MenuItem key={project.uuid} value={project.uuid}>
                                        {project.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth className="filter-field">
                            <InputLabel shrink>Utilisateur</InputLabel>
                            <Select
                                value={filters.userUuid || "ALL"}
                                onChange={(e) => handleFilterChange({ userUuid: e.target.value })}
                                displayEmpty
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Person className="filter-field-icon" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="ALL">
                                    {filters.projectUuid && filters.projectUuid !== "ALL"
                                        ? "Tous les participants"
                                        : "Tous les utilisateurs"}
                                </MenuItem>
                                {filteredUsers?.map((user) => (
                                    <MenuItem key={user.uuid} value={user.uuid}>
                                        {user.firstname} {user.lastname}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth className="filter-field">
                            <InputLabel shrink>Type</InputLabel>
                            <Select
                                value={filters.type || "ALL"}
                                onChange={(e) => handleFilterChange({ type: e.target.value as any })}
                                displayEmpty
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Category className="filter-field-icon" />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="ALL">Tous les types</MenuItem>
                                <MenuItem value="CONTRIBUTION">Encaissements</MenuItem>
                                <MenuItem value="WITHDRAWAL">Retraits</MenuItem>
                                <MenuItem value="PENALTY">Pénalités</MenuItem>
                            </Select>
                        </FormControl>

                        <div className="date-filters-row">
                            <DatePicker
                                label="Date début"
                                value={filters.startDate ? dayjs(filters.startDate) : null}
                                onChange={(date: Dayjs | null) =>
                                    handleFilterChange({ startDate: date?.format('YYYY-MM-DD') })
                                }
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        className: "filter-date-field",
                                        InputProps: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <DateRange className="filter-field-icon" />
                                                </InputAdornment>
                                            ),
                                        }
                                    }
                                }}
                            />

                            <DatePicker
                                label="Date fin"
                                value={filters.endDate ? dayjs(filters.endDate) : null}
                                onChange={(date: Dayjs | null) =>
                                    handleFilterChange({ endDate: date?.format('YYYY-MM-DD') })
                                }
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        className: "filter-date-field",
                                        InputProps: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <DateRange className="filter-field-icon" />
                                                </InputAdornment>
                                            ),
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tableau des transactions */}
            <Card className="table-card">
                <CardContent>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : transactions.length === 0 ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <p>{getEmptyMessage()}</p>
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Utilisateur</TableCell>
                                            <TableCell>Projet</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Mois</TableCell>
                                            <TableCell align="right">Montant</TableCell>
                                            <TableCell>Statut</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((transaction) => (
                                            <TableRow key={transaction.uuid}>
                                                <TableCell>
                                                    {new Date(transaction.createdAt).toLocaleDateString('fr-FR')}
                                                </TableCell>
                                                <TableCell>{transaction.userName}</TableCell>
                                                <TableCell>{transaction.projectName}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getTypeLabel(transaction.type)}
                                                        color={getTypeColor(transaction.type)}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiChip-label': {
                                                                color: 'white'
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{transaction.month || "-"}</TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatAmount(transaction.amount, transaction.currency)}</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(transaction.status)}
                                                        color={getStatusColor(transaction.status)}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiChip-label': {
                                                                color: 'white'
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Lignes par page:"
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}